/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { css } from '@emotion/react';

import React, { ReactElement, ReactNode } from 'react';
import { ButtonItem } from 'utils/models';
import { ButtonGroup } from '../ButtonGroup';

type MonetisationSectionCardProps = {
  children: ReactNode;
};

export const MonetisationSectionCard = ({
  children,
}: MonetisationSectionCardProps): ReactElement => (
  <div css={sectionCardCss}>{children}</div>
);

type MonetisationSectionBodyProps = {
  children: ReactNode;
};

/** Body rows below section header — spacing via row padding only */
export const MonetisationSectionBody = ({
  children,
}: MonetisationSectionBodyProps): ReactElement => (
  <div css={sectionBodyCss}>{children}</div>
);

type MonetisationSectionRowProps = {
  children: ReactNode;
  className?: string;
};

export const MonetisationSectionRow = ({
  children,
  className,
}: MonetisationSectionRowProps): ReactElement => (
  <div css={sectionRowCss} className={className}>
    {children}
  </div>
);

type MonetisationSectionBlockRowProps = {
  children: ReactNode;
  className?: string;
};

/** Full-width row (e.g. multiline misc-ads copy) */
export const MonetisationSectionBlockRow = ({
  children,
  className,
}: MonetisationSectionBlockRowProps): ReactElement => (
  <div css={sectionBlockRowCss} className={className}>
    {children}
  </div>
);

export const SubRowLabel = ({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}): ReactElement => (
  <div
    css={[
      tw`whitespace-nowrap flex-shrink-0`,
      enabled ? tw`text-sonnant-dark` : tw`text-sonnant-grey-5`,
    ]}
  >
    {children}
  </div>
);

type MonetisationPlacementRowProps = {
  label: string;
  enabled: boolean;
  items: ButtonItem[];
  defaultValue?: ButtonItem['value'];
  onChange: (item: ButtonItem) => void;
};

/** UI-only: renders the label + ButtonGroup row shared by the placement settings. Selection handling stays with the caller's `onChange`. */
export const MonetisationPlacementRow = ({
  label,
  enabled,
  items,
  defaultValue,
  onChange,
}: MonetisationPlacementRowProps): ReactElement => (
  <MonetisationSectionRow
    css={!enabled ? [sectionRowWrapCss, monetisationDisabledSubItemsCss] : sectionRowWrapCss}
  >
    <SubRowLabel enabled={enabled}>{label}</SubRowLabel>
    <ButtonGroup items={items} onChange={onChange} defaultValue={defaultValue} />
  </MonetisationSectionRow>
);

const sectionCardCss = css`
  ${tw`rounded-lg bg-white py-4 px-5 flex flex-col h-full`}
  border: 1px solid lightgrey;
`;

const sectionBodyCss = css`
  ${tw`flex flex-col w-full`}
`;

const sectionRowCss = css`
  ${tw`flex justify-between items-center py-3 min-h-[4rem] box-border`}
`;

const sectionBlockRowCss = css`
  ${tw`py-3 text-sonnant-dark box-border`}
`;

const sectionRowWrapCss = css`
  ${tw`flex-wrap gap-[10px]`}
`;

export const monetisationDisabledSubItemsCss = css`
  ${tw`pointer-events-none`}
`;
